import type {
  DocumentTab,
  TabHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import { useCallback, useMemo, useRef } from 'react'

export type TabLeaveGuard = (
  leave: () => void,
  destination: TabHistoryItem | undefined
) => void

export type RegisterTabLeaveGuard = (
  tabId: string,
  guard: TabLeaveGuard
) => () => void

type TabNavigation = {
  pushToHistory: (tabId: string, item: TabHistoryItem) => void
  goBack: (tabId: string) => void
  goForward: (tabId: string) => void
  removeTab: (tabId: string) => boolean
}

type GuardedTabNavigation = Omit<TabNavigation, 'removeTab'> & {
  removeTab: (tabId: string, onRemoved?: () => void) => void
  registerTabLeaveGuard: RegisterTabLeaveGuard
}

function historyStep(
  openTabs: DocumentTab[],
  tabId: string,
  step: number
): TabHistoryItem | undefined {
  const tab = openTabs.find(({ id }) => id === tabId)
  return tab?.history[tab.currentIndex + step]
}

export function useTabLeaveGuards(
  navigation: TabNavigation & { openTabs: DocumentTab[] }
): GuardedTabNavigation {
  const tabIdToGuard = useRef(new Map<string, TabLeaveGuard>())
  const openTabsRef = useRef(navigation.openTabs)
  openTabsRef.current = navigation.openTabs
  const registerTabLeaveGuard = useCallback<RegisterTabLeaveGuard>(
    (tabId, guard) => {
      tabIdToGuard.current.set(tabId, guard)
      return () => {
        if (tabIdToGuard.current.get(tabId) === guard)
          tabIdToGuard.current.delete(tabId)
      }
    },
    []
  )
  const guarded = useCallback(
    (
      tabId: string,
      destination: TabHistoryItem | undefined,
      leave: () => void
    ): void => {
      const guard = tabIdToGuard.current.get(tabId)
      return guard ? guard(leave, destination) : leave()
    },
    []
  )
  const step = useCallback(
    (tabId: string, offset: number) =>
      historyStep(openTabsRef.current, tabId, offset),
    []
  )
  const { pushToHistory, goBack, goForward, removeTab } = navigation
  return useMemo(
    () => ({
      pushToHistory: (tabId, item) =>
        guarded(tabId, item, () => pushToHistory(tabId, item)),
      goBack: (tabId) => guarded(tabId, step(tabId, -1), () => goBack(tabId)),
      goForward: (tabId) =>
        guarded(tabId, step(tabId, 1), () => goForward(tabId)),
      removeTab: (tabId, onRemoved) =>
        guarded(tabId, undefined, () => {
          if (removeTab(tabId)) onRemoved?.()
        }),
      registerTabLeaveGuard,
    }),
    [
      goBack,
      goForward,
      guarded,
      pushToHistory,
      registerTabLeaveGuard,
      removeTab,
      step,
    ]
  )
}
